/**
 * ExportToCloudModal Component
 * Lets users export a message response as a DOCX to Google Drive or Dropbox.
 * Remembers the last-used folder per provider so it opens there next time.
 * Reuses cloud-picker styles from data-bundles module.
 *
 * Google Drive: writing the new file needs only Google's `basic` level
 * (drive.file); listing the person's own folders needs `read`. Without it the
 * export goes to My Drive and the folder area asks for `read`, in place.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Spin, message as antMessage } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { CloudStorageApi } from '@/modules/data-bundles/services/cloudStorageService';
import { useCloudConnections } from '@/modules/data-bundles/hooks/useCloudConnections';
import { GoogleAccessPrompt } from '@/modules/cloud-storage/components/GoogleAccessPrompt';
import { useGoogleReadGate } from '@/modules/cloud-storage/hooks/useGoogleReadGate';
import type { CloudFolder } from '@/modules/data-bundles/types';
import '@/modules/data-bundles/components/CloudFilePicker.css';
import './ExportToCloudModal.css';

interface ExportToCloudModalProps {
  visible: boolean;
  content: string;
  defaultFileName?: string;
  onClose: () => void;
}

interface BreadcrumbItem {
  id: string | null;
  name: string;
}

const GoogleDriveIcon = () => (
  <svg viewBox="0 0 87.3 78" width="16" height="14" style={{ verticalAlign: 'middle', marginInlineEnd: 6 }}>
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z" fill="#00ac47" />
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
  </svg>
);

const DropboxIcon = () => (
  <svg viewBox="0 0 43.35 40.3" width="16" height="14" style={{ verticalAlign: 'middle', marginInlineEnd: 6 }}>
    <path fill="#0061FF" d="M12.87 0L0 8.14l8.8 7.07 12.87-8.14zM0 22.28l12.87 8.14 8.8-7.07L8.8 15.21zM21.67 23.35l8.8 7.07L43.35 22.28l-8.8-7.07zM43.35 8.14L30.47 0l-8.8 7.07 12.87 8.14zM21.72 24.77l-8.85 7.07-3.79-2.47v2.77l12.64 7.58 12.64-7.58v-2.77l-3.79 2.47z" />
  </svg>
);

const PROVIDERS = [
  { key: 'google_drive', label: 'Google Drive', icon: <GoogleDriveIcon /> },
  { key: 'dropbox', label: 'Dropbox', icon: <DropboxIcon /> },
];

export const ExportToCloudModal: React.FC<ExportToCloudModalProps> = ({
  visible,
  content,
  defaultFileName,
  onClose,
}) => {
  const { t } = useLanguage();
  const { connections, loadConnections, isConnected } = useCloudConnections();
  const [activeProvider, setActiveProvider] = useState<string>('');
  const [folders, setFolders] = useState<CloudFolder[]>([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<CloudFolder | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Track whether default folder was already applied for this provider session
  const defaultAppliedFor = useRef<string>('');
  const gate = useGoogleReadGate(visible && activeProvider === 'google_drive');
  // Google without `read`: no folder listing — the export goes to My Drive.
  const googleWithoutRead = gate.needsRead;
  const accessPending = gate.pending;

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      loadConnections();
      setSelectedFolder(null);
      setBreadcrumbs([{ id: null, name: 'Root' }]);
      setCurrentFolderId(null);
      setFolders([]);
      setFileName(defaultFileName || t('export.defaultFileName'));
      setExporting(false);
      setActiveProvider('');
      gate.reset();
      defaultAppliedFor.current = '';
    }
  }, [visible, loadConnections, defaultFileName, t]);

  // Lock body scroll while the modal is open so the chat behind it doesn't move
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  // Close on Escape
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, onClose]);

  // Auto-select first connected provider
  useEffect(() => {
    if (visible && connections.length > 0 && !activeProvider) {
      const firstConnected = PROVIDERS.find((p) => isConnected(p.key));
      if (firstConnected) {
        setActiveProvider(firstConnected.key);
      }
    }
  }, [visible, connections, activeProvider, isConnected]);

  // When provider becomes active, try to load the saved default folder
  useEffect(() => {
    if (!activeProvider || !visible || accessPending) return;
    if (googleWithoutRead) {
      setBreadcrumbs([{ id: null, name: 'Root' }]);
      setCurrentFolderId(null);
      setSelectedFolder({ folder_id: 'root', name: t('chat.drive.myDrive') } as CloudFolder);
      return;
    }
    if (defaultAppliedFor.current === activeProvider) return;

    defaultAppliedFor.current = activeProvider;

    CloudStorageApi.getDefaultExportFolder(activeProvider)
      .then((saved) => {
        if (saved && saved.breadcrumbs?.length) {
          // Restore saved browsing position exactly
          setBreadcrumbs(saved.breadcrumbs);
          // folder_id is the parent we were browsing in (empty string = root)
          setCurrentFolderId(saved.folder_id || null);
        } else {
          setBreadcrumbs([{ id: null, name: 'Root' }]);
          setCurrentFolderId(null);
        }
      })
      .catch(() => {
        // Silently fall back to root
        setBreadcrumbs([{ id: null, name: 'Root' }]);
        setCurrentFolderId(null);
      });
  }, [activeProvider, visible, accessPending, googleWithoutRead]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load folder contents when provider or folder changes
  useEffect(() => {
    if (activeProvider && visible && !accessPending && !googleWithoutRead) {
      if (selectedFolder?.folder_id === 'root') setSelectedFolder(null);
      loadFolders(activeProvider, currentFolderId);
    }
  }, [activeProvider, currentFolderId, visible, accessPending, googleWithoutRead]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFolders = useCallback(
    async (provider: string, parentId: string | null) => {
      setLoadingContent(true);
      try {
        const folderList = await CloudStorageApi.listFolders(provider, parentId || undefined);
        setFolders(folderList);
      } catch (error) {
        setFolders([]);
        if (!gate.refusedBy(error)) console.error('Error loading cloud folders:', error);
      } finally {
        setLoadingContent(false);
      }
    },
    []
  );

  const handleProviderChange = (providerKey: string) => {
    setActiveProvider(providerKey);
    setSelectedFolder(null);
    defaultAppliedFor.current = '';  // allow default folder fetch for new provider
  };

  const handleFolderNavigate = (folder: CloudFolder) => {
    setCurrentFolderId(folder.folder_id);
    setBreadcrumbs((prev) => [...prev, { id: folder.folder_id, name: folder.name }]);
    setSelectedFolder(null);
  };

  const handleBreadcrumbClick = (index: number) => {
    const item = breadcrumbs[index];
    setCurrentFolderId(item.id);
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
    setSelectedFolder(null);
  };

  const handleFolderSelect = (folder: CloudFolder) => {
    setSelectedFolder((prev) =>
      prev?.folder_id === folder.folder_id ? null : folder
    );
  };

  /** Save the browsing position so next time the selected folder is visible. */
  const saveDefaultFolder = (provider: string) => {
    // currentFolderId = the folder we're browsing IN (parent of listed items).
    // breadcrumbs = navigation path to this location.
    // On load we restore both → user sees the same folder listing.
    const parentId = currentFolderId || '';  // empty string = root
    const parentName = breadcrumbs.length > 1
      ? breadcrumbs[breadcrumbs.length - 1].name
      : 'Root';
    CloudStorageApi.setDefaultExportFolder(
      provider,
      parentId,
      parentName,
      breadcrumbs,
    ).catch((err) => console.warn('Failed to save default export folder:', err));
  };

  const handleExport = async () => {
    if (!selectedFolder || !activeProvider) return;

    setExporting(true);
    try {
      const result = await CloudStorageApi.exportToCloud(
        activeProvider,
        content,
        selectedFolder.folder_id,
        fileName.trim() || t('export.defaultFileName'),
      );

      // Remember this browsing position for next time
      saveDefaultFolder(activeProvider);

      antMessage.success(t('export.success'));

      if (result.web_view_link) {
        window.open(result.web_view_link, '_blank');
      }

      onClose();
    } catch (error: any) {
      console.error('Export error:', error);
      antMessage.error(error?.response?.data?.error || t('export.error'));
    } finally {
      setExporting(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await CloudStorageApi.downloadAsDocx(
        content,
        fileName.trim() || t('export.defaultFileName'),
      );
      antMessage.success(t('export.downloadSuccess'));
      onClose();
    } catch (error: any) {
      console.error('Download error:', error);
      antMessage.error(t('export.downloadError'));
    } finally {
      setDownloading(false);
    }
  };

  if (!visible) return null;

  const connectedProviders = PROVIDERS.filter((p) => isConnected(p.key));

  return createPortal(
    <div className="cloud-picker-overlay export-cloud-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cloud-picker-container export-cloud-popup" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cloud-picker-header">
          <div>
            <h2 className="cloud-picker-title">{t('export.title')}</h2>
            <p className="cloud-picker-subtitle">{t('export.subtitle')}</p>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {connectedProviders.length > 0 ? (
          <>
            {/* Provider Tabs */}
            <div className="cloud-picker-tabs">
              {connectedProviders.map((provider) => (
                <button
                  key={provider.key}
                  type="button"
                  className={`cloud-picker-tab ${activeProvider === provider.key ? 'active' : ''}`}
                  onClick={() => handleProviderChange(provider.key)}
                >
                  {provider.icon}{provider.label}
                </button>
              ))}
            </div>

            {/* File Name Input */}
            <div className="export-filename-row">
              <label className="export-filename-label">{t('export.fileName')}</label>
              <input dir="auto"
                type="text"
                className="export-filename-input"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder={t('export.fileNamePlaceholder')}
              />
              <span className="export-filename-ext">.docx</span>
            </div>

            {/* Breadcrumbs */}
            <div className="cloud-picker-breadcrumbs">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <span className="breadcrumb-separator">/</span>}
                  <button
                    type="button"
                    className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
                    onClick={() => handleBreadcrumbClick(index)}
                  >
                    {crumb.name}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Folder Content */}
            <div className="cloud-picker-content">
              {loadingContent || accessPending ? (
                <div className="cloud-picker-loading">
                  <Spin size="large" />
                </div>
              ) : googleWithoutRead ? (
                <GoogleAccessPrompt
                  access="read"
                  reason="folders"
                  variant="card"
                  onGranted={gate.reset}
                />
              ) : folders.length === 0 ? (
                <div className="cloud-picker-empty">
                  <p>{t('export.noFolders')}</p>
                </div>
              ) : (
                folders.map((folder) => (
                  <div
                    key={folder.folder_id}
                    className={`cloud-picker-item cloud-picker-folder ${
                      selectedFolder?.folder_id === folder.folder_id ? 'selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="export-folder"
                      checked={selectedFolder?.folder_id === folder.folder_id}
                      onChange={() => handleFolderSelect(folder)}
                      className="cloud-picker-checkbox"
                    />
                    <div
                      className="cloud-picker-item-icon folder-icon"
                      onClick={() => handleFolderSelect(folder)}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>
                    <span
                      className="cloud-picker-item-name"
                      onClick={() => handleFolderSelect(folder)}
                      style={{ flex: 1, cursor: 'pointer' }}
                    >
                      {folder.name}
                    </span>
                    <button
                      type="button"
                      className="folder-navigate-btn"
                      onClick={() => handleFolderNavigate(folder)}
                      title={t('export.openFolder')}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="cloud-picker-footer">
              <span className="cloud-picker-selection-count" dir="auto">
                {selectedFolder ? selectedFolder.name : t('export.noFolderSelected')}
              </span>
              <div className="cloud-picker-footer-actions">
                <button
                  type="button"
                  className="btn-download"
                  onClick={handleDownload}
                  disabled={downloading || exporting || !fileName.trim()}
                >
                  {downloading ? (
                    <span className="export-btn-loading">
                      <Spin size="small" />
                      {t('export.downloading')}
                    </span>
                  ) : (
                    t('export.download')
                  )}
                </button>
                <button type="button" className="btn-cancel" onClick={onClose} disabled={exporting || downloading}>
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleExport}
                  disabled={!selectedFolder || exporting || downloading || !fileName.trim()}
                >
                  {exporting ? (
                    <span className="export-btn-loading">
                      <Spin size="small" />
                      {t('export.exporting')}
                    </span>
                  ) : (
                    t('export.exportButton')
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="cloud-picker-no-connections">
            <div className="cloud-picker-no-connections-icon">
              <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
              </svg>
            </div>
            <h3>{t('export.noConnections')}</h3>
            <p>{t('export.noConnectionsHint')}</p>

            {/* File name + download even without cloud connections */}
            <div className="export-filename-row" style={{ width: '100%', borderBottom: 'none', justifyContent: 'center' }}>
              <label className="export-filename-label">{t('export.fileName')}</label>
              <input dir="auto"
                type="text"
                className="export-filename-input"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder={t('export.fileNamePlaceholder')}
                style={{ maxWidth: 200 }}
              />
              <span className="export-filename-ext">.docx</span>
            </div>

            <div className="cloud-picker-footer-actions" style={{ marginTop: 8 }}>
              <button type="button" className="btn-cancel" onClick={onClose}>
                {t('common.close')}
              </button>
              <button
                type="button"
                className="btn-download"
                onClick={handleDownload}
                disabled={downloading || !fileName.trim()}
              >
                {downloading ? (
                  <span className="export-btn-loading">
                    <Spin size="small" />
                    {t('export.downloading')}
                  </span>
                ) : (
                  t('export.download')
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
